import { Omit } from '@kaa/ui-flanders/components/utilities';
import { motion, MotionProps, useAnimation } from 'framer-motion';
import React, { HTMLAttributes, useEffect, useRef } from 'react';

type MotionDivProps = MotionProps &
  Omit<HTMLAttributes<HTMLDivElement>, 'defaultValue'> & {
    testid?: string;
    isOpen: boolean;
  };

const FOCUSABLE_SELECTOR = 'a, input, select, textarea, button, [tabindex]';

export const MotionDiv = ({
  testid,
  isOpen,
  children,
  ...props
}: MotionDivProps) => {
  const controls = useAnimation();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    controls.stop();
    if (isOpen) {
      controls.start('open');
    } else {
      controls.start('close');
    }

    return () => {
      controls.stop();
    };
  }, [isOpen, controls]);

  // the open/close animation only collapses height/overflow, which doesnt
  // remove focusable elements from the tabbing. Toggle it manually so
  // keyboard/screen-reader users cant land on fields that are hidden
  useEffect(() => {
    if (!ref.current) return;

    ref.current
      .querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      .forEach((element) => {
        if (isOpen) {
          element.removeAttribute('tabindex');
        } else {
          element.setAttribute('tabindex', '-1');
        }
      });
  }, [isOpen]);

  return (
    <motion.div
      ref={ref}
      data-testid={testid}
      initial={isOpen ? 'open' : 'close'}
      animate={controls}
      aria-hidden={!isOpen}
      {...props}
    >
      {children}
    </motion.div>
  );
};
